function Brand() {
  return (
    <div className="flex items-center gap-2.5 pb-1">
      <span className="flex size-7 items-center justify-center rounded-md border border-edge bg-ink-800">
        <span className="size-2 rounded-full bg-sync" />
      </span>
      <span className="mono text-sm font-semibold tracking-[0.35em] text-zinc-100">DATUM</span>
    </div>
  )
}

/** Route helper for the two static legal pages (plan T6.1, public launch). */
export function legalPageFor(pathname: string): 'privacy' | 'terms' | null {
  const clean = pathname.replace(/\/+$/, '')
  if (clean === '/privacy') return 'privacy'
  if (clean === '/terms') return 'terms'
  return null
}

function LegalShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-ink-950 text-zinc-100">
      <header className="flex h-12 items-center justify-between border-b border-edge bg-ink-900 px-4">
        <a href="/" className="no-underline"><Brand /></a>
        <a href="/" className="mono text-[11px] text-zinc-500 hover:text-zinc-200">← back to app</a>
      </header>
      <main className="mx-auto max-w-2xl px-6 py-10">
        <h1 className="mono mb-1 text-[11px] uppercase tracking-[0.25em] text-zinc-500">Legal</h1>
        <h2 className="mb-2 text-xl font-semibold tracking-tight text-zinc-50">{title}</h2>
        <p className="mono mb-8 text-[11px] text-zinc-600">Effective 2026-11-03 · v0.1.0</p>
        <div className="flex flex-col gap-6 text-[13px] leading-relaxed text-zinc-300">{children}</div>
      </main>
    </div>
  )
}

function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mono mb-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-zinc-200">{heading}</h3>
      <div className="flex flex-col gap-2">{children}</div>
    </section>
  )
}

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy">
      <Section heading="What Datum is">
        <p>
          Datum is a geolocated technical note-taking tool for field crews. It stores notes you
          write, the position captured when you create them, and optional photos and voice notes.
        </p>
      </Section>
      <Section heading="Data we store">
        <ul className="list-disc pl-5">
          <li><strong>Account</strong> — name, email address, hashed password, session records.</li>
          <li><strong>Notes</strong> — text content, edit history (version numbers), AI-generated summaries and tags you trigger.</li>
          <li><strong>Location</strong> — coordinates, altitude and accuracy attached to each note, plus named waypoints you create. Location is captured from your device only when you create or edit a note; Datum does not track your movement.</li>
          <li><strong>Media</strong> — photos and audio you explicitly attach. Audio is transcribed on your request; the transcription is stored with the attachment.</li>
        </ul>
      </Section>
      <Section heading="Where it lives and who processes it">
        <p>
          Data is hosted on Cloudflare infrastructure (D1 database, R2 object storage) in the United
          States. When you press the AI button, the note text and any attached audio are processed by
          Cloudflare Workers AI models to produce a summary, tags and transcript. Nothing is sent to
          third-party advertisers or analytics — Datum contains no tracking, no cookies beyond the
          authentication session, and no third-party scripts.
        </p>
      </Section>
      <Section heading="Your control">
        <p>
          You can export everything Datum holds about your account at any time (the gear icon →
          Data &amp; account → download export), and permanently delete your account, notes,
          organizations and media from the same panel. Deletion removes database rows immediately;
          automated backups roll off within 30 days.
        </p>
      </Section>
      <Section heading="Security">
        <p>
          Traffic is HTTPS-only with HSTS; secrets are stored hashed; media access requires
          short-lived signed URLs; organizations are private to their members. No system is
          unbreakable — report concerns to the operator via the contact below.
        </p>
      </Section>
      <Section heading="Contact">
        <p>
          The operator of this Datum deployment is Built Networks. Reach out through the deployed
          instance at{' '}
          <a href="https://datum.builtnetworks.com" className="text-sync underline">
            datum.builtnetworks.com
          </a>{' '}
          (until the custom domain is live:{' '}
          <a href="https://datum.curtislamasters.workers.dev" className="text-sync underline">
            datum.curtislamasters.workers.dev
          </a>
          ) — or handle anything urgent yourself: export and delete are self-service.
        </p>
      </Section>
    </LegalShell>
  )
}

export function TermsPage() {
  return (
    <LegalShell title="Terms of Service">
      <Section heading="The service">
        <p>
          Datum provides offline-capable, geolocated note-taking for field teams. It is offered
          “as is” for internal and crew use.
        </p>
      </Section>
      <Section heading="Your account">
        <p>
          You are responsible for keeping your credentials secure and for activity under your
          account. Delete your account any time from the Data &amp; account panel; deletion is
          final.
        </p>
      </Section>
      <Section heading="Acceptable use">
        <p>
          Don’t use Datum to store unlawful content, to harass, or to attempt to disrupt or
          reverse-engineer the service beyond its own client. You own the notes you write; by
          using the service you permit the infrastructure described in the Privacy Policy to
          process them.
        </p>
      </Section>
      <Section heading="AI output">
        <p>
          Summaries, tags and transcriptions are machine-generated suggestions. They can be wrong.
          Nothing in Datum should be relied on for safety, regulatory, engineering-acceptance or
          medical decisions without human verification.
        </p>
      </Section>
      <Section heading="Availability">
        <p>
          The service may change, pause or disappear without notice. Export your data regularly if
          it matters — offline clients keep working against the last synced state.
        </p>
      </Section>
      <Section heading="Liability">
        <p>
          To the maximum extent permitted by law, the operator is not liable for data loss, lost
          profits or indirect damages arising from use of Datum.
        </p>
      </Section>
      <Section heading="Changes">
        <p>
          Continued use after these terms change on the deployed instance constitutes acceptance.
          Material changes will be flagged in-app when practical.
        </p>
      </Section>
    </LegalShell>
  )
}
